import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Building2, Code2, History, KeyRound, Target } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { authApi } from "@/features/auth/api";
import AuditLogTab from "@/features/settings/tabs/AuditLogTab";
import LeadScoringTab from "@/features/settings/tabs/LeadScoringTab";
import ProvidersTab from "@/features/settings/tabs/ProvidersTab";
import TeamTab from "@/features/settings/tabs/TeamTab";
import WorkspaceTab from "@/features/settings/tabs/WorkspaceTab";
import ApiKeysPanel from "@/components/ApiKeysPanel";
import { apiKeysApi } from "@/features/settings/api";

const DEFAULT_NOTIF_PREFS = {
  email_new_calls: true,
  email_agent_errors: true,
  sms_critical_alerts: false,
  email_weekly_summary: true,
};

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const role = user?.role ?? "member";
  const isAdmin = hasMinRole(role, "admin");
  const isManager = hasMinRole(role, "manager");
  // A customer has an account here but no part in running the tenant: their
  // Settings is their own name, password and notifications. The admin tabs
  // already fall away with the role ladder; the audit log is the one that
  // would otherwise show them the tenant's activity.
  const isCustomer = role === "customer";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
      </div>

      <Tabs defaultValue={searchParams.get("tab") ?? "profile"} className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="providers">
              <KeyRound className="size-3.5" />
              API Providers
            </TabsTrigger>
          )}
          {isManager && (
            <TabsTrigger value="workspace">
              <Building2 className="size-3.5" />
              Workspace
            </TabsTrigger>
          )}
          {isManager && (
            <TabsTrigger value="lead-scoring">
              <Target className="size-3.5" />
              Lead Scoring
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          {isAdmin && <TabsTrigger value="team">Team</TabsTrigger>}
          {isAdmin && (
            <TabsTrigger value="api-keys">
              <Code2 className="size-3.5" />
              API Keys
            </TabsTrigger>
          )}
          {!isCustomer && (
            <TabsTrigger value="audit">
              <History className="size-3.5" />
              Audit Log
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile">
          <ProfileTab onUserUpdated={setUser} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="providers">
            <ProvidersTab />
          </TabsContent>
        )}

        {isManager && (
          <TabsContent value="workspace">
            <WorkspaceTab />
          </TabsContent>
        )}

        {isManager && (
          <TabsContent value="lead-scoring">
            <LeadScoringTab />
          </TabsContent>
        )}

        <TabsContent value="notifications">
          <NotificationsTab />
        </TabsContent>

        <TabsContent value="security">
          <SecurityTab />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="team">
            <TeamTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="api-keys">
            <ApiKeysPanel endpoints={apiKeysApi} />
          </TabsContent>
        )}

        {!isCustomer && (
          <TabsContent value="audit">
            <AuditLogTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ── Profile ─────────────────────────────────────────────────────────────────
function ProfileTab({
  onUserUpdated,
}: {
  onUserUpdated: (u: ReturnType<typeof useAuthStore.getState>["user"]) => void;
}) {
  const user = useAuthStore((s) => s.user);
  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");
  const [phone, setPhone] = useState(
    (user as unknown as { phone?: string })?.phone ?? "",
  );

  const save = useMutation({
    mutationFn: () =>
      authApi.updateMe({
        first_name: firstName,
        last_name: lastName,
        phone,
      }),
    onSuccess: (u) => {
      toast.success("Profile updated");
      onUserUpdated(u);
    },
    onError: () => toast.error("Failed to save profile"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile Information</CardTitle>
      </CardHeader>
      <CardContent className="max-w-md space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="first">First name</Label>
          <Input
            id="first"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="last">Last name</Label>
          <Input
            id="last"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" value={user?.email ?? ""} disabled />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save Changes"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Notifications ───────────────────────────────────────────────────────────
function NotificationsTab() {
  const [prefs, setPrefs] = useState(DEFAULT_NOTIF_PREFS);

  const q = useQuery({
    queryKey: ["notification-prefs"],
    queryFn: authApi.notificationPrefs,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (q.data) {
      setPrefs((p) => ({ ...p, ...q.data }));
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: (data: Record<string, boolean>) =>
      authApi.updateNotificationPrefs(data),
    onError: () => toast.error("Failed to save preference"),
  });

  function toggle(key: keyof typeof DEFAULT_NOTIF_PREFS) {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    save.mutate({ [key]: next[key] });
  }

  if (q.isLoading) return <Skeleton className="h-40 w-full" />;

  const ITEMS: { key: keyof typeof DEFAULT_NOTIF_PREFS; label: string }[] = [
    { key: "email_new_calls", label: "Email notifications for new calls" },
    {
      key: "email_agent_errors",
      label: "Email notifications for agent errors",
    },
    { key: "sms_critical_alerts", label: "SMS notifications for critical alerts" },
    { key: "email_weekly_summary", label: "Weekly summary emails" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Notification Preferences</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {ITEMS.map((it) => (
          <label
            key={it.key}
            className="flex cursor-pointer items-center justify-between rounded-md border bg-card px-3 py-2"
          >
            <span className="text-sm">{it.label}</span>
            <Switch
              checked={prefs[it.key]}
              onCheckedChange={() => toggle(it.key)}
            />
          </label>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Security ────────────────────────────────────────────────────────────────
function SecurityTab() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const mfaEnabled = !!(user as unknown as { mfa_enabled?: boolean })
    ?.mfa_enabled;

  const [pwdOpen, setPwdOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Security Settings</CardTitle>
          <CardDescription>
            Password and two-factor authentication.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex max-w-md flex-col gap-2">
          <Button variant="outline" onClick={() => setPwdOpen(true)}>
            Change Password
          </Button>
          <Button variant="outline" onClick={() => setMfaOpen(true)}>
            {mfaEnabled
              ? "Manage Two-Factor Authentication"
              : "Enable Two-Factor Authentication"}
          </Button>
        </CardContent>
      </Card>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      <MfaDialog
        open={mfaOpen}
        onOpenChange={setMfaOpen}
        enabled={mfaEnabled}
        onChanged={(enabled) => {
          if (user) {
            setUser({
              ...user,
              ...({ mfa_enabled: enabled } as Record<string, unknown>),
            } as ReturnType<typeof useAuthStore.getState>["user"]);
          }
        }}
      />
    </>
  );
}

function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError("");
    }
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      authApi.changePassword({
        current_password: current,
        new_password: next,
        new_password_confirm: confirm,
      }),
    onSuccess: () => {
      toast.success("Password changed");
      onOpenChange(false);
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(
        e?.response?.data?.error?.message || "Failed to change password.",
      );
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
        </DialogHeader>
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError("");
            if (next !== confirm) {
              setError("New passwords do not match.");
              return;
            }
            save.mutate();
          }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="cur">Current password</Label>
            <Input
              id="cur"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new">New password</Label>
            <Input
              id="new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="conf">Confirm new password</Label>
            <Input
              id="conf"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={save.isPending || !current || !next}
            >
              {save.isPending ? "Changing…" : "Change Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type MfaStep = "idle" | "qr" | "backup" | "disable";

function MfaDialog({
  open,
  onOpenChange,
  enabled,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  enabled: boolean;
  onChanged: (enabled: boolean) => void;
}) {
  const [step, setStep] = useState<MfaStep>("idle");
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState("");

  function reset() {
    setStep("idle");
    setSecret("");
    setQr("");
    setCode("");
    setBackupCodes([]);
    setError("");
  }

  useEffect(() => {
    if (open) reset();
  }, [open]);

  const setupM = useMutation({
    mutationFn: () => authApi.mfaSetup(),
    onSuccess: (data) => {
      setSecret(data.secret);
      setQr(data.qr_code_url);
      setStep("qr");
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(
        e?.response?.data?.error?.message || "Failed to start MFA setup.",
      );
    },
  });

  const verifyM = useMutation({
    mutationFn: (c: string) => authApi.mfaVerify(c),
    onSuccess: (data) => {
      setBackupCodes(data.backup_codes || []);
      setStep("backup");
      setCode("");
      toast.success("Two-factor authentication enabled");
      onChanged(true);
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(
        e?.response?.data?.error?.message || "Invalid code. Try again.",
      );
    },
  });

  const disableM = useMutation({
    mutationFn: (c: string) => authApi.mfaDisable(c),
    onSuccess: () => {
      toast.info("Two-factor authentication disabled");
      onChanged(false);
      onOpenChange(false);
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(
        e?.response?.data?.error?.message || "Invalid code. Try again.",
      );
    },
  });

  function title() {
    if (step === "backup") return "Save Your Backup Codes";
    if (step === "disable") return "Disable Two-Factor Authentication";
    return "Two-Factor Authentication";
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title()}</DialogTitle>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {step === "idle" && !qr && (
          <div className="space-y-3">
            {enabled ? (
              <>
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">
                  Two-factor authentication is enabled on your account.
                </div>
                <p className="text-sm text-muted-foreground">
                  To disable 2FA, enter the 6-digit code from your
                  authenticator app.
                </p>
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    setStep("disable");
                    setError("");
                    setCode("");
                  }}
                >
                  Disable Two-Factor Authentication
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Add an extra layer of security to your account. You&apos;ll
                  need an authenticator app like Google Authenticator, Authy,
                  or 1Password.
                </p>
                <Button
                  onClick={() => setupM.mutate()}
                  disabled={setupM.isPending}
                >
                  {setupM.isPending
                    ? "Starting…"
                    : "Set Up Two-Factor Authentication"}
                </Button>
              </>
            )}
          </div>
        )}

        {step === "qr" && (
          <div className="space-y-3">
            <p className="text-sm">
              Scan this QR code with your authenticator app, then enter the
              6-digit code below to confirm.
            </p>
            <div className="text-center">
              <img
                src={qr}
                alt="MFA QR Code"
                className="mx-auto h-48 w-48"
              />
            </div>
            <p className="text-center text-xs text-muted-foreground">
              Can&apos;t scan? Enter this key manually:
              <br />
              <strong className="break-all font-mono text-foreground">
                {secret}
              </strong>
            </p>
            <Input
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              maxLength={6}
              inputMode="numeric"
              placeholder="000000"
              autoFocus
              className="text-center text-lg tracking-[0.5em] font-mono"
            />
          </div>
        )}

        {step === "backup" && (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Save these backup codes in a safe place. Each can only be used
              once.
            </div>
            <div className="grid grid-cols-2 gap-1.5 rounded-md bg-muted p-3 font-mono text-sm">
              {backupCodes.map((c, i) => (
                <div key={i}>{c}</div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(backupCodes.join("\n"));
                toast.success("Backup codes copied");
              }}
            >
              Copy to Clipboard
            </Button>
          </div>
        )}

        {step === "disable" && (
          <Input
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            maxLength={6}
            inputMode="numeric"
            placeholder="000000"
            autoFocus
            className="text-center text-lg tracking-[0.5em] font-mono"
          />
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {step === "backup" ? "Done" : "Cancel"}
          </Button>
          {step === "qr" && (
            <Button
              disabled={verifyM.isPending || code.length !== 6}
              onClick={() => verifyM.mutate(code)}
            >
              {verifyM.isPending ? "Verifying…" : "Verify & Enable"}
            </Button>
          )}
          {step === "disable" && (
            <Button
              disabled={disableM.isPending || code.length !== 6}
              onClick={() => disableM.mutate(code)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disableM.isPending ? "Disabling…" : "Disable"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

